import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import DateRangeIcon from '@mui/icons-material/DateRange'
import ErrorIcon from '@mui/icons-material/Error'
import LoyaltyIcon from '@mui/icons-material/Loyalty'
import LuggageIcon from '@mui/icons-material/Luggage'
import MedicationLiquidIcon from '@mui/icons-material/MedicationLiquid'
import PersonIcon from '@mui/icons-material/Person'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import SportsBaseballIcon from '@mui/icons-material/SportsBaseball'
import StickyNoteIcon from '@mui/icons-material/StickyNote2'
import { SvgIconOwnProps, SvgIconProps, useTheme } from '@mui/material'
import Stack from '@mui/material/Stack'
import { customColors } from '@themes/theme'

import { SxProps } from '@mui/material/styles'
import { BoardingIcon } from 'asset/boarding_icon'
import { BoneIcon } from 'asset/bone_icon'
import { CatIconEx } from 'asset/cat_icon'
import { DaycareIcon } from 'asset/daycare_icon'
import { DogIconEx } from 'asset/dog_icon'
import { GroomingIcon } from 'asset/grooming_icon'
import { PackageIcon } from 'asset/package_icon'
import { SofaIcon } from 'asset/sofa_icon'
import { TemperTestIcon } from 'asset/temper_test_icon'
import { TrainingIcon } from 'asset/training_icon'

type TIconsSizes = 'small' | 'medium' | 'large'
interface TIconAvatarProps {
  icon: JSX.Element
  fillColor?: string
  backgroundColor?: string
  size?: TIconsSizes
  disabled?: boolean
  disabledFillColor?: string
  disabledBackgroundColor?: string
  sx?: SxProps
  reverseColors?: boolean
  fontSize?: string
}
type TAvatarProps = Pick<
  TIconAvatarProps,
  'fillColor' | 'backgroundColor' | 'size' | 'disabled' | 'sx' | 'fontSize' | 'disabledBackgroundColor'
>
const iconSizes = {
  small: '24px',
  medium: '32px',
  large: '40px',
}

export const IconAvatar = ({
  icon,
  fillColor,
  backgroundColor,
  size = 'medium',
  disabled = false,
  disabledFillColor,
  disabledBackgroundColor,
  sx,
  reverseColors = false,
}: TIconAvatarProps) => {
  const theme = useTheme()
  fillColor = disabled ? disabledFillColor || theme.palette.action.disabled : fillColor || theme.palette.primary.main
  backgroundColor = disabled
    ? disabledBackgroundColor || theme.palette.action.disabledBackground
    : backgroundColor || theme.palette.primary.shade12
  const _size = iconSizes[size] ?? iconSizes.large

  if (reverseColors) {
    backgroundColor = fillColor
    fillColor = theme.palette.getContrastText(theme.palette.primary.main)
  }

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        ...sx,
        backgroundSize: 'contain',
        backgroundColor,
        height: _size,
        width: _size,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignContent: 'center',
        '& svg': {
          fill: fillColor,
        },
      }}
    >
      {icon}
    </Stack>
  )
}

export const AddCircleIconAvatar = (props: TAvatarProps) => (
  <IconAvatar {...props} icon={<AddCircleIcon viewBox="-1 -0 25 25" />} />
)

export const PersonIconAvatar = (props: TAvatarProps) => <IconAvatar {...props} icon={<PersonIcon />} />
export const BoardingIconAvatar = (props: TAvatarProps) => (
  <IconAvatar {...props} icon={<BoardingIcon viewBox="1 1 19 19" />} />
)
export const CalendarIconAvatar = () => <IconAvatar icon={<DateRangeIcon />} />
export const PerksIconAvatar = (props: TAvatarProps) => (
  <IconAvatar {...props} icon={<BoneIcon viewBox="-3 -2 25 25" />} />
)
export const BaseballIconAvatar = () => (
  <IconAvatar icon={<SportsBaseballIcon sx={{ transform: 'rotate(-20deg)' }} />} />
)
export const EmptyIconAvatar = (props: TAvatarProps) => <IconAvatar {...props} icon={<svg />} />
export const LuggageIconAvatar = () => <IconAvatar icon={<LuggageIcon />} />
export const StickyNoteIconAvatar = () => <IconAvatar icon={<StickyNoteIcon />} />
export const AssignmentAvatarIcon = () => <IconAvatar icon={<AssignmentTurnedInIcon />} />
export const PetAddOnIconAvatar = (props: TAvatarProps) => (
  <IconAvatar {...props} icon={<SofaIcon viewBox="-3 -2 25 25" />} />
)
export const InvalidDrawerIcon = () => {
  const theme = useTheme()
  return (
    <IconAvatar
      icon={<ErrorIcon />}
      fillColor={theme.palette.error.main}
      backgroundColor={theme.palette.error.shade8}
    />
  )
}
export const AccountIconAvatar = (props: TAvatarProps) => <IconAvatar {...props} icon={<AccountCircleIcon />} />
export const TrainingIconAvatar = (props: TAvatarProps) => {
  const viewBox = props.fontSize === 'small' ? '-9 -8 38 38' : '-4 -3 28 28'

  return <IconAvatar {...props} icon={<TrainingIcon viewBox={viewBox} />} />
}

export const BoardingAvatar = (props: TAvatarProps) => {
  const theme = useTheme()
  const { fontSize, ...rest } = props
  return (
    <IconAvatar
      {...rest}
      icon={<BoardingIcon viewBox="-2 -2 24 24" fontSize={fontSize as SvgIconOwnProps['fontSize']} />}
      fillColor={theme.palette.primary.dark}
      backgroundColor={theme.palette.primary.shade12}
    />
  )
}

export const PackageAvatar = (props: TAvatarProps) => {
  const theme = useTheme()
  const { fontSize, ...rest } = props
  return (
    <IconAvatar
      {...rest}
      icon={<PackageIcon fontSize={fontSize as SvgIconOwnProps['fontSize']} />}
      fillColor={theme.palette.primary.dark}
      backgroundColor={theme.palette.primary.shade12}
    />
  )
}

export const DaycareAvatar = (props: TAvatarProps) => {
  const { fillColor } = props
  return <IconAvatar {...props} icon={<DaycareIcon fill={fillColor} />} />
}

export const DaycareAvatarGeese = (props: TAvatarProps) => {
  const theme = useTheme()
  const { fontSize, ...rest } = props

  const baseColor = props.disabled ? theme.palette.action.disabled : props.fillColor || theme.palette.warning.main
  const bgColor = props.backgroundColor || customColors.lightSecondary

  return (
    <IconAvatar
      {...rest}
      icon={<DaycareIcon fill={baseColor} fontSize={fontSize as SvgIconOwnProps['fontSize']} />}
      fillColor={baseColor}
      backgroundColor={bgColor}
    />
  )
}

export const TemperTestAvatar = (props: TAvatarProps) => {
  const theme = useTheme()
  const { fontSize, ...rest } = props
  return (
    <IconAvatar
      icon={<TemperTestIcon fontSize={fontSize as SvgIconOwnProps['fontSize']} />}
      fillColor={theme.palette.success.main}
      backgroundColor={theme.palette.success.shade8}
      {...rest}
    />
  )
}

export const GroomingAvatar = (props: TAvatarProps) => {
  const theme = useTheme()
  const { fontSize, ...rest } = props

  const baseColor = props.disabled ? theme.palette.action.disabled : props.fillColor || customColors.deepPurple
  const bgColor = props.backgroundColor || customColors.deepPurple50

  return (
    <IconAvatar
      {...rest}
      icon={<GroomingIcon viewBox="-2 -3 24 24" fontSize={fontSize as SvgIconOwnProps['fontSize']} />}
      fillColor={baseColor}
      backgroundColor={bgColor}
    />
  )
}

export const TrainingAvatar = (props: TAvatarProps) => {
  const theme = useTheme()
  const { fontSize, ...rest } = props
  const viewBox = fontSize === 'small' ? '-9 -8 38 38' : '-4 -3 28 28'

  const baseColor = props.disabled ? theme.palette.action.disabled : props.fillColor || theme.palette.primary.main
  const bgColor = props.backgroundColor

  return (
    <IconAvatar
      {...rest}
      icon={<TrainingIcon viewBox={viewBox} fontSize={fontSize as SvgIconOwnProps['fontSize']} />}
      fillColor={baseColor}
      backgroundColor={bgColor}
    />
  )
}

export const LoyaltyIconAvatar = (props: TAvatarProps) => {
  const theme = useTheme()
  const { fontSize, ...rest } = props
  return (
    <IconAvatar
      {...rest}
      icon={<LoyaltyIcon viewBox="-2 -3 24 24" fontSize={fontSize as SvgIconOwnProps['fontSize']} />}
      fillColor={theme.palette.primary.main}
      backgroundColor={theme.palette.primary.shade12}
    />
  )
}

export const WalletIconAvatar = (props: TAvatarProps) => {
  const theme = useTheme()
  const { fontSize, ...rest } = props
  return (
    <IconAvatar
      {...rest}
      icon={<AccountBalanceWalletIcon viewBox="-2 -3 24 24" fontSize={fontSize as SvgIconOwnProps['fontSize']} />}
      fillColor={theme.palette.primary.main}
      backgroundColor={theme.palette.primary.shade12}
    />
  )
}

interface TServiceTypeAvatar extends TAvatarProps {
  serviceType?: string
  color?: string
  fill?: string
  width?: number
  height?: number
  fontSize?: string
  fillColor?: string
}

export const ServiceTypeAvatar = (props: TServiceTypeAvatar) => {
  const { serviceType } = props

  switch (serviceType) {
    case 'boarding':
      return <BoardingAvatar {...props} />
    case 'daycare':
      return <DaycareAvatarGeese {...props} />
    case 'temper-test':
      return <TemperTestAvatar {...props} />
    case 'packages':
      return <PackageAvatar {...props} />
    case 'grooming':
      return <GroomingAvatar {...props} />
    case 'memberships':
      return <LoyaltyIconAvatar {...props} />
    case 'wallet':
      return <WalletIconAvatar {...props} />
    case 'training':
      return <TrainingIconAvatar {...props} />
    default:
      return <EmptyIconAvatar {...props} />
  }
}

export const ServiceTypeIcon = (props: TServiceTypeAvatar) => {
  const theme = useTheme()
  const { serviceType, color, fill, ...restProps } = props
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const mainColor = theme.palette[color || 'primary']?.main || theme.palette.primary.main
  switch (serviceType) {
    case 'boarding':
      return <BoardingIcon {...(restProps as SvgIconProps)} viewBox="-2 -2 24 24" />
    case 'daycare':
      return <DaycareIcon {...(restProps as SvgIconProps)} fill={fill || mainColor} />
    case 'temper-test':
      return <TemperTestIcon {...(restProps as SvgIconProps)} />
    case 'packages':
      return <PackageIcon {...(restProps as SvgIconProps)} />
    case 'grooming':
      return <GroomingIcon {...(restProps as SvgIconProps)} viewBox="-4 -3 24 24" />
    case 'wallet':
      return <AccountBalanceWalletIcon {...(restProps as SvgIconProps)} viewBox="-4 -4 28 28" />
    case 'training':
      return <TrainingIcon {...(restProps as SvgIconProps)} viewBox="-4 -3 28 28" />
    default:
      return <EmptyIconAvatar {...restProps} />
  }
}

interface TSpecieAvatarProps extends TAvatarProps {
  specie: string
  reverseColors: boolean
}

export const SpecieAvatar = (props: TSpecieAvatarProps) => {
  const { specie, size, reverseColors, disabled, ...rest } = props
  switch (specie) {
    case 'dog':
      return (
        <IconAvatar
          size={size}
          reverseColors={reverseColors}
          disabled={disabled}
          icon={<DogIconEx {...rest} fontSize={size} />}
        />
      )
    case 'cat':
      return (
        <IconAvatar
          size={size}
          reverseColors={reverseColors}
          disabled={disabled}
          icon={<CatIconEx {...rest} fontSize={size} />}
        />
      )
    default:
      return (
        <IconAvatar
          size={size}
          reverseColors={reverseColors}
          disabled={disabled}
          icon={<QuestionMarkIcon {...rest} fontSize={size} />}
        />
      )
  }
}

export const FeedMedsAvatar = (props: TAvatarProps) => {
  const theme = useTheme()
  const { fontSize, ...rest } = props

  const baseColor = props.disabled ? theme.palette.action.disabled : props.fillColor
  const bgColor = props.backgroundColor

  return (
    <IconAvatar
      {...rest}
      icon={<MedicationLiquidIcon viewBox="-2 0 24 24" fontSize={fontSize as SvgIconOwnProps['fontSize']} />}
      fillColor={baseColor}
      backgroundColor={bgColor}
    />
  )
}
